import { forwardRef } from "react";
import { cn } from "../lib/cn";
import { useFieldControlProps } from "../molecules/Field";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Error state — error border + aria-invalid. */
  invalid?: boolean;
}

/** Multi-line text input. Content-sized (min-h-24), no autosize. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      invalid = false,
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...props
    },
    ref,
  ) => {
    const fieldControlProps = useFieldControlProps({
      id,
      describedBy: ariaDescribedBy,
      invalid: ariaInvalid || invalid,
      required: ariaRequired,
    });
    const resolvedInvalid = Boolean(fieldControlProps["aria-invalid"]);

    return (
      <textarea
        ref={ref}
        data-slot="textarea"
        {...fieldControlProps}
        className={cn(
          "w-full min-h-24 rounded-lg border border-border-control bg-surface-default px-3 py-2 type-body-lg text-content-primary placeholder:text-content-disabled transition-colors",
          "outline-none focus-visible:border-border-focused focus-visible:ring-2 focus-visible:ring-border-focused-subtle",
          "disabled:cursor-not-allowed disabled:opacity-38",
          resolvedInvalid &&
            "border-surface-error focus-visible:border-surface-error",
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";
